import '@/TokamapEditor/ux/AreaSidebar/NormalHoverToggle.scss';

import ColorButton                   from '@/TokamapEditor/ux/AreaSidebar/ColorButton';
import BorderButton                  from '@/TokamapEditor/ux/AreaSidebar/BorderButton';
import SettingsButton                from '@/TokamapEditor/ux/AreaSidebar/SettingsPopover';
import { StrokeWidthChoice }         from '@/TokamapEditor/ux/AreaSidebar/BorderButton';
import Segmented                     from '@widesk-ui/Segmented';
import { InlineColorPalette }        from '@/TokamapEditor/ux/AreaSidebar/ColorButton';
import Button                        from '@widesk-ui/components/Button';
import MixedSelect                   from '@/TokamapEditor/ux/AreaSidebar/MixedSelect';
import View                          from '@widesk-ui/components/View';
import Field                         from '@/TokamapEditor/ux/AreaSidebar/Field';
import NormalHoverToggle             from '@/TokamapEditor/ux/AreaSidebar/NormalHoverToggle';
import useHoverPreview               from '@/TokamapEditor/helpers/useHoverPreview';
import { type NormalHoverScope }     from '@/TokamapEditor/ux/AreaSidebar/NormalHoverToggle';
import currentEditorStore            from '@/TokamapEditor/stores/currentEditorStore';
import { metersToUnit }              from '@/TokamapEditor/helpers/mapKind';
import { unitToMeters }              from '@/TokamapEditor/helpers/mapKind';
import { observer }                  from 'mobx-react-lite';
import { DrawTriggerOptions }        from '@tokamap/core';
import { type Shape }                from '@tokamap/core';
import { type ShapeStyle }           from '@tokamap/core';
import SwitchField                   from '@/TokamapEditor/ux/SwitchField';
import { commonValue }               from '@/TokamapEditor/ux/AreaSidebar/multiSelectHelpers';
import React                         from 'react';
import useDesignTokens               from '@/hooks/useDesignTokens';
import { DisconnectOutlined }        from '@ant-design/icons';
import { EditOutlined }              from '@ant-design/icons';
import { EyeOutlined }               from '@ant-design/icons';
import leftSidebarStore              from '@/TokamapEditor/stores/leftSidebarStore';
import themeStore                    from '@/TokamapEditor/stores/themeStore';
import onboardingStore               from '@/TokamapEditor/stores/onboardingStore';
import type { ThemeStyle }            from '@/TokamapEditor/stores/themeStore';
import ThemeSwatch                   from '@/TokamapEditor/ux/ThemeSwatch';
import ReliefButton                  from '@/TokamapEditor/ux/AreaSidebar/ThreeDOptions';
import GroundButton                  from '@/TokamapEditor/ux/AreaSidebar/GroundPickers';
import SectionTitle                  from '@/TokamapEditor/ux/AreaSidebar/SectionTitle';
import CollapsiblePanel              from '@/TokamapEditor/ux/AreaSidebar/CollapsiblePanel';
import HelpTip                       from '@/TokamapEditor/ux/AreaSidebar/HelpTip';
import asSingleHistoryEntry          from '@/TokamapEditor/helpers/asSingleHistoryEntry';
import Checkbox                      from '@widesk-ui/components/form/Checkbox';
import { themeStyleLock }            from '@/TokamapEditor/helpers/themeImposed';
import { themeValueLock }            from '@/TokamapEditor/helpers/themeImposed';
import { zoomAppearanceOptions }     from '@/TokamapEditor/helpers/zoomAppearance';
import { zoomFactorLabel }           from '@/TokamapEditor/helpers/zoomAppearance';
import { mercatorToLngLat }          from '@tokamap/core';
import { rectAroundPoints }          from '@tokamap/core';
import { shapesExtentPoints }        from '@tokamap/core';

const STROKE_WIDTHS = [1, 2, 4, 8, 12];

const STATE_NOTES: Record<NormalHoverScope, string> = {
	normal: `Les couleurs et l'épaisseur ci-dessous s'appliquent à l'état normal.`,
	hover: `Les couleurs et l'épaisseur ci-dessous s'appliquent quand le visiteur survole la forme.`,
};

const AreaThemeSection = observer(() => {
	const [scope, setScope] = React.useState<NormalHoverScope>('normal');

	useHoverPreview(scope === 'hover');

	const themed = currentEditorStore.sidebarShapes.some(shape => !!shape.themeId);

	return (
		<View gap="lg">
			{/* En premier : c'est lui qui décide si les réglages suivants s'appliquent. `|| themed` : si
			    le projet n'a plus de thème mais qu'une forme en référence encore un (thème supprimé), le
			    champ reste affiché — sinon plus aucun moyen de la délier. */}
			{(!!themeStore.themes.length || themed) && <ThemeField />}

			<View gap="md">
				<NormalHoverToggle value={scope} onChange={setScope} help={STATE_NOTES[scope]} />

				<Fields key={scope} hover={scope === 'hover'} />
			</View>

			<AdvancedOptions />
		</View>
	);
});

export default AreaThemeSection;

/**
 * Lie la ou les formes sélectionnées à un thème (`Shape.themeId`). Les réglages que le thème impose
 * restent affichés mais verrouillés, les autres se règlent toujours sur la forme.
 */
const ThemeField = observer(() => {
	const editor = currentEditorStore.get;
	const shapes = currentEditorStore.sidebarShapes;

	if (!shapes.length) return null;

	// Même piège que `pathPriority` plus bas : `commonValue` ne distingue pas "aucune forme n'a de
	// thème" de "mélange", les deux renvoyant `undefined` — d'où le `Set` dédié.
	const mixed = new Set(shapes.map(s => s.themeId)).size > 1;
	// `undefined` et non `''` : seul `undefined` ne correspond à aucune option et laisse le placeholder
	// s'afficher (voir le commentaire de `MixedSelect`).
	const themeId = shapes[0]!.themeId;
	const themed = shapes.some(shape => !!shape.themeId);
	const theme = !mixed && themeId ? themeStore.getTheme(themeId) : undefined;

	const setThemeId = (value: string | undefined) => editor.setShapeThemeId(shapes.map(s => s.id), value);

	return (
		<Field
			label={(
				<>
					Thème
					<HelpTip>
						Ce que le thème impose est verrouillé sur la forme ; le reste se règle ici. Les réglages
						propres à la forme sont conservés et reviennent si vous retirez le thème.
					</HelpTip>
				</>
			)}
		>
			<View gap="xs">
				<MixedSelect
					value={themeId}
					mixed={mixed}
					placeholder="Aucun thème"
					onChange={value => setThemeId(value ? String(value) : undefined)}
					// Plus d'option "Aucun" : délier passe par le bouton ci-dessous, plus explicite qu'une
					// entrée de liste à retrouver parmi les thèmes.
					options={themeStore.themes.map(theme => ({
						label: (
							<View row gap="xs" centerV>
								<ThemeSwatch theme={theme} />

								{theme.name}
							</View>
						),
						value: theme.id,
					}))}
				/>

				{themed && (
					// Liens plutôt que boutons bordés, mais AVEC l'icône : seule, la mention textuelle passait
					// inaperçue. `padding: 0` pour que les textes finissent au ras des bords du select.
					<View row spread centerV>
						{theme ? (
							<Button
								type="link"
								size="small"
								icon={<EditOutlined />}
								style={{ padding: 0, height: 'auto' }}
								onClick={() => leftSidebarStore.openThemeForm(theme, { fromShape: true })}
							>
								Modifier le thème
							</Button>
						) : <span />}

						<Button
							type="link"
							size="small"
							icon={<DisconnectOutlined />}
							style={{ padding: 0, height: 'auto' }}
							onClick={() => setThemeId(undefined)}
						>
							Retirer le thème
						</Button>
					</View>
				)}
			</View>
		</Field>
	);
});

const AdvancedOptions = observer(() => {
	const editor = currentEditorStore.get;
	const shapes = currentEditorStore.sidebarShapes;

	if (!shapes.length) return null;

	const locked = commonValue(shapes, s => !!s.locked);
	const lockedMixed = locked === undefined;

	const setLocked = (checked: boolean) => {
		editor.setShapeLocked(shapes.map(s => s.id), checked);
	};

	return (
		<CollapsiblePanel title="Options avancées">
			<SwitchField
				label={<>Verrouillée<HelpTip>La forme ne peut plus être déplacée, copiée ou supprimée.</HelpTip></>}
				value={!lockedMixed && !!locked}
				mixed={lockedMixed}
				onChange={setLocked}
			/>
		</CollapsiblePanel>
	);
});

const VisibilityButton = observer(() => {
	const tokens = useDesignTokens();
	const shapes = currentEditorStore.sidebarShapes;

	if (!shapes.length) return null;

	const lock = themeValueLock(shapes)('minZoomOffset');
	const offsets = new Set(shapes.map(s => s.minZoomOffset));
	const offset = lock ? lock.style.minZoomOffset : shapes[0]!.minZoomOffset;
	const mixed = !lock && offsets.size > 1;

	const roads = shapes.filter(s => s.ground === true || s.ground === 'water').length;
	const water = shapes.filter(s => s.ground === 'water' || s.ground === 'waterOnly').length;
	const kept = (count: number, label: string) => (count === shapes.length ? label : count ? `${label} pour une partie` : undefined);

	return (
		<SettingsButton
			label="Visibilité"
			summary={mixed ? 'Plusieurs valeurs' : offset ? `Dès le zoom ${zoomFactorLabel(offset)}` : 'Toujours visible'}
			details={[kept(roads, 'routes par-dessus'), kept(water, 'eau par-dessus')].filter((detail): detail is string => !!detail)}
			mixed={mixed}
			themed={!!lock}
			width={280}
			preview={<EyeOutlined style={{ fontSize: 22, color: tokens.text.secondary, flexShrink: 0 }} />}
		>
			<MinZoomOffsetField />
			<GroundField />
		</SettingsButton>
	);
});

const MinZoomOffsetField = observer(() => {
	const editor = currentEditorStore.get;
	const shapes = currentEditorStore.sidebarShapes;
	const referenceZoom = editor.mapAdapter.getStartCamera()?.zoom ?? currentEditorStore.documentOrigin?.zoom;

	if (!shapes.length) return null;

	const lock = themeValueLock(shapes)('minZoomOffset');
	const mixed = !lock && new Set(shapes.map(s => s.minZoomOffset)).size > 1;
	const offset = lock ? lock.style.minZoomOffset : shapes[0]!.minZoomOffset;

	const showAppearanceZoom = (minZoomOffset: number | undefined) => {
		if (referenceZoom === undefined) return;

		const rect = rectAroundPoints(shapesExtentPoints(shapes), 0);

		editor.mapAdapter.animateToView({
			zoom: referenceZoom + (minZoomOffset ?? 0),
			...(rect ? { center: mercatorToLngLat(rect.center) } : {}),
		});
	};

	return (
		<Field
			lockedBy={lock}
			label={(
				<>
					Apparition au zoom
					<HelpTip>
						La forme n'apparaît qu'une fois la carte zoomée d'autant depuis sa vue de départ, dans l'aperçu
						et la carte publiée. Dans l'éditeur, elle reste affichée mais estompée tant que ce zoom n'est
						pas atteint.
					</HelpTip>
				</>
			)}
		>
			<MixedSelect
				value={offset ?? ''}
				mixed={mixed}
				options={zoomAppearanceOptions(offset)}
				onChange={value => {
					const minZoomOffset = typeof value === 'number' ? value : undefined;

					editor.setShapeMinZoomOffset(shapes.map(s => s.id), minZoomOffset);
					showAppearanceZoom(minZoomOffset);
				}}
			/>
		</Field>
	);
});

const Fields = observer(({ hover }: { hover?: boolean; }) => {
	const editor = currentEditorStore.get;
	const tokens = useDesignTokens();

	const shapes = currentEditorStore.sidebarShapes;
	const isLine = shapes.some(s => s.shapeKind === 'line');

	const apply = async (
		name: string,
		v: string | number | boolean | undefined,
		options?: DrawTriggerOptions,
		remove = false,
	) => {
		const method = hover ? 'setShapeHoverStyle' : 'setShapeStyle';
		const value: ShapeStyle = remove ? {
			bgColor: undefined,
			borderColor: undefined,
			borderWidth: undefined,
			borderWidthUnit: undefined,
			borderDashed: undefined,
		} : { [name]: v };

		const ids = shapes.map(s => s.id);

		if (!remove) return editor[method](ids, value, options);

		// Réinitialiser le survol rend aussi ses hauteurs à celles du repos, en un seul Annuler.
		await asSingleHistoryEntry(() => {
			editor[method](ids, value, options);
			editor.setShapeHoverHeight(ids, undefined, options);
			editor.setShapeHoverElevation(ids, undefined, options);
		});
	};

	const styleKey = hover ? 'hoverStyle' : 'style';
	const lock = themeStyleLock(shapes, !!hover);
	const lockedBy = (key: keyof ThemeStyle) => lock(key);
	const widthLock = lock('borderWidth');

	const style = {
		borderColor: lock('borderColor')?.style.borderColor ?? commonValue(shapes, s => s[styleKey]?.borderColor),
		bgColor: lock('bgColor')?.style.bgColor ?? commonValue(shapes, s => s[styleKey]?.bgColor),
		borderWidth: widthLock ? widthLock.style.borderWidth : commonValue(shapes, s => s[styleKey]?.borderWidth),
		borderWidthUnit: widthLock ? (widthLock.style.borderWidthUnit ?? 'px') : commonValue(shapes, s => s[styleKey]?.borderWidthUnit ?? 'px'),
		borderDashed: lock('borderDashed') ? lock('borderDashed')!.style.borderDashed : commonValue(shapes, s => s[styleKey]?.borderDashed),
	};

	// `undefined` (mixte) est distingué de "non défini pour toutes les formes" (`false`) : une forme
	// qui n'a jamais eu de style au survol a `hoverStyle` absent partout, donc `commonValue` renvoie
	// `undefined` pour CHAQUE propriété dans ce cas précis — indiscernable d'un vrai mélange sans
	// cette vérification dédiée.
	const isMixed = (key: 'borderColor' | 'bgColor' | 'borderWidth' | 'borderDashed') => {
		if (shapes.length < 2 || lockedBy(key)) return false;
		const values = shapes.map(s => s[styleKey]?.[key]);
		return new Set(values).size > 1;
	};

	const isCreated = !!hover && (
		[style.borderColor, style.bgColor, style.borderWidth, style.borderDashed].some(Boolean)
		|| shapes.some(s => s.hoverHeight !== undefined || s.hoverElevation !== undefined)
	);
	const inMeters = style.borderWidthUnit === 'm';
	const lengthUnit = currentEditorStore.lengthUnit;

	const borderMixed = isMixed('borderColor') || isMixed('borderWidth') || isMixed('borderDashed');
	const borderWidthLabel = inMeters
		? `${metersToUnit(style.borderWidth ?? unitToMeters(1, lengthUnit), lengthUnit)} ${lengthUnit}`
		: `${style.borderWidth || 1} px`;
	const borderSummary = borderMixed ? 'Plusieurs valeurs' : `${borderWidthLabel} · ${style.borderDashed ? 'Pointillés' : 'Plein'}`;

	return (
		<View gap="lg" className={hover ? 'normal-hover-fields--hover' : 'normal-hover-fields--normal'}>
			{hover && isCreated && (
				<View row spread centerV>
					<View color={tokens.text.muted} size={12}>Style au survol personnalisé</View>

					<Button size="small" onClick={() => apply('borderColor', '', {}, true)}>
						Réinitialiser
					</Button>
				</View>
			)}

			<View gap="sm">
				<SectionTitle>Apparence</SectionTitle>

				<View style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 8 }}>
					{!isLine && (
						<span className={hover ? undefined : onboardingStore.targetClass('color')} style={{ display: 'flex', minWidth: 0 }}>
							<ColorButton
								label="Fond"
								block
								alpha
								lockedBy={lockedBy('bgColor')}
								value={style?.bgColor}
								mixed={isMixed('bgColor')}
								onChange={color => apply('bgColor', color)}
							/>
						</span>
					)}

					<BorderButton
						summary={borderSummary}
						color={style?.borderColor}
						width={inMeters ? undefined : style?.borderWidth}
						dashed={style?.borderDashed}
						mixed={borderMixed}
						themed={!!(lockedBy('borderColor') || widthLock || lockedBy('borderDashed'))}
					>
						{lockedBy('borderColor') ? (
							<Field label="Couleur">
								<View row>
									<ColorButton
										label={style?.borderColor?.toUpperCase() ?? 'Couleur du thème'}
										lockedBy={lockedBy('borderColor')}
										value={style?.borderColor}
										onChange={color => apply('borderColor', color)}
									/>
								</View>
							</Field>
						) : (
							<Field label="Couleur">
								<InlineColorPalette
									alpha
									value={style?.borderColor}
									mixed={isMixed('borderColor')}
									onChange={color => apply('borderColor', color)}
								/>
							</Field>
						)}

						<Field label="Épaisseur" lockedBy={widthLock}>
							<StrokeWidthChoice
								options={STROKE_WIDTHS.map(width => (inMeters
									? { label: String(metersToUnit(width / 10, lengthUnit)), value: width / 10 }
									: { label: String(width), value: width }))}
								value={isMixed('borderWidth') ? undefined : (style?.borderWidth || 1)}
								onChange={width => apply('borderWidth', width)}
								unit={{
									value: inMeters ? 'm' : 'px',
									options: [
										{ value: 'px', label: 'px' },
										{ value: 'm', label: lengthUnit },
									],
									title: `px : même épaisseur à l'écran, quel que soit le zoom. ${lengthUnit} : épaisseur au sol, qui suit le zoom.`,
									onChange: unit => editor[hover ? 'setShapeHoverStyle' : 'setShapeStyle'](
										shapes.map(s => s.id),
										{ borderWidthUnit: unit === 'm' ? 'm' : undefined, borderWidth: unit === 'm' ? 0.1 : 1 },
									),
								}}
							/>
						</Field>

						<Field label="Trait" lockedBy={lockedBy('borderDashed')}>
							<Segmented
								block
								value={isMixed('borderDashed') ? undefined : (style?.borderDashed ? 'dashed' : 'solid')}
								onChange={value => apply('borderDashed', value === 'dashed' || undefined)}
								options={[
									{ value: 'solid', label: 'Plein' },
									{ value: 'dashed', label: 'Pointillés' },
								]}
							/>
						</Field>
					</BorderButton>

					{!hover && <GroundButton shapes={shapes} />}

					<ReliefButton hover={hover} />

					{!hover && (
						<div style={{ gridColumn: '1 / -1', minWidth: 0 }}>
							<VisibilityButton />
						</div>
					)}
				</View>
			</View>
		</View>
	);
});

const GroundField = observer(() => {
	const editor = currentEditorStore.get;
	const shapes = currentEditorStore.sidebarShapes;
	const roads = commonValue(shapes, s => s.ground === true || s.ground === 'water');
	const water = commonValue(shapes, s => s.ground === 'water' || s.ground === 'waterOnly');

	const toggle = (layer: 'roads' | 'water', checked: boolean) => asSingleHistoryEntry(() => {
		const byGround = new Map<Shape['ground'], string[]>();

		for (const shape of shapes) {
			const keepsRoads = layer === 'roads' ? checked : shape.ground === true || shape.ground === 'water';
			const keepsWater = layer === 'water' ? checked : shape.ground === 'water' || shape.ground === 'waterOnly';
			const ground = groundOf(keepsRoads, keepsWater);

			if (ground !== (shape.ground || undefined)) byGround.set(ground, [...(byGround.get(ground) ?? []), shape.id]);
		}

		byGround.forEach((ids, ground) => editor.setShapeGround(ids, ground));
	});

	return (
		<Field label={<>Par-dessus la forme<HelpTip>Ce que le fond de carte garde visible par-dessus la forme, pour colorer une zone au sol.</HelpTip></>}>
			<View gap={4}>
				<Checkbox value={roads} onChange={checked => toggle('roads', !!checked)}>
					Routes et chemins
				</Checkbox>

				<Checkbox value={water} onChange={checked => toggle('water', !!checked)}>
					Eau
				</Checkbox>
			</View>
		</Field>
	);
});

function groundOf(roads: boolean, water: boolean): Shape['ground'] {
	if (roads && water) return 'water';
	if (water) return 'waterOnly';

	return roads || undefined;
}
